/**
 * Country-specific features sent by the backend via GET /company.
 * Mirrors ICountryFeatures in packages/crealobackend/src/countries/types.ts.
 * Components use this to drive conditional rendering instead of hardcoded country checks.
 */

export type BankingFieldKey =
    | "iban"
    | "bic"
    | "transit_number"
    | "institution_number"
    | "account_number"
    | "sort_code"
    | "bacs_sun";

export type InternationalBankingFieldKey =
    | "intl_swift"
    | "intl_account_number"
    | "intl_routing_code"
    | "intl_bank_name";

/** Mirrors PaymentFormatId in the backend. Add new values here when adding a new format. */
export type PaymentFormatId =
    | "ISO20022_PAIN001"
    | "ISO20022_PAIN001_UK"
    | "ISO20022_PAIN001_UK_CHAPS"
    | "ISO20022_PAIN001_UK_FPS"
    | "BACS_STD18"
    | "CPA_EFT_005"
    | "WISE_CSV_UK"
    | "ISO20022_PAIN001_INTL";

export type StatementBlockKey = "vat" | "precount" | "withholding" | "ircec";

export type BankingStatusCondition =
    | { type: "sameCountry" }
    | { type: "zone"; zone: "SEPA" }
    | { type: "countries"; codes: string[] }
    | { type: "default" };

export interface BankingStatusRule {
    condition: BankingStatusCondition;
    status: "local" | "foreign_local" | "foreign_international";
}

export interface CountryFeatures {
    /** Whether the URSSAF / Declarations page should be shown */
    urssaf: boolean;

    /** Available VAT options (empty array = no VAT for this country) */
    vat: { options: { value: number; label: string }[] };

    /** Available precount options (empty array = no precount for this country) */
    precount: { options: { value: number; label: string }[] };

    /** Banking fields to display in the beneficiary RIB section */
    bankingFields: BankingFieldKey[];

    /**
     * Banking fields that apply only to the company (originator), not to beneficiaries.
     * Shown in Settings > Bank Information. Absent = no company-only fields.
     */
    companyBankingFields?: BankingFieldKey[];

    /**
     * How precount is determined for this country.
     * - 'social_profile_type': derived from URSSAF socialProfileType (France)
     * - 'direct': user selects precount directly (Belgium, Canada)
     */
    precountMode: "social_profile_type" | "direct";

    /** Statement rendering config */
    statementBlocks: {
        showNet: boolean;
        showPrecount: boolean;
        netBlocks: StatementBlockKey[];
    };

    /** Validation rules surfaced for field display and hints */
    validation: {
        siretLength: number | null;
        socialSecurityFormat: string | null;
        bankingFields?: {
            institution_number?: { length: number };
            transit_number?: { length: number };
            account_number?: { length?: number; minLength?: number; maxLength?: number };
            sort_code?: { length: number };
            bacs_sun?: { length: number };
        };
    };

    /**
     * Whether to display "prix TTC" on title forms.
     * false for Canada (no book tax — HT = TTC).
     */
    showPriceTTC: boolean;

    /**
     * When true, numeric values in statements use a dot as decimal separator (e.g. 3.14)
     * instead of a comma (e.g. 3,14). Applied to UK and other anglophone countries.
     */
    usePointDecimalSeparator: boolean;

    /**
     * Payment format identifiers supported by this country, in display order.
     * When length > 1 the export modal shows a format selector step.
     */
    supportedPaymentFormatIds: PaymentFormatId[];

    /**
     * i18n key for the "local" banking mode label shown when international banking is enabled.
     * Backend sends the right key per country (e.g. "PaymentMode.LocalMode.SEPA" for France).
     * Falls back to a derived key when absent.
     */
    localBankingModeLabel?: string;

    /**
     * Payment format identifier used to generate the international wire file.
     * When defined and the setting is enabled, the backend generates a separate international wire file alongside the local file.
     * When absent, no international wire file is generated (and a warning is shown in the export modal).
     */
    internationalPaymentFormatId?: PaymentFormatId;

    /**
     * Priority-ordered rules that derive bankingStatus from the bank account country.
     * When defined and international_banking_enabled is active, a bank account country selector
     * replaces the manual Foreign Banking toggle.
     */
    bankingStatusRules?: BankingStatusRule[];
}

/** Default features (France) used as fallback before API response */
export const defaultCountryFeatures: CountryFeatures = {
    urssaf: true,
    vat: {
        options: [
            { value: 0, label: "Beneficiars.VATNo" },
            { value: 1, label: "Beneficiars.VATYes" },
            { value: 2, label: "Beneficiars.VATExemption" },
            { value: 3, label: "Beneficiars.VATTwentyPercent" },
        ],
    },
    precount: {
        options: [
            { value: 0, label: "Beneficiars.PrecountNo" },
            { value: 1, label: "Beneficiars.PrecountYes" },
        ],
    },
    bankingFields: ["iban", "bic"],
    precountMode: "social_profile_type",
    statementBlocks: { showNet: true, showPrecount: true, netBlocks: ["vat", "precount", "ircec"] },
    validation: { siretLength: 14, socialSecurityFormat: null },
    showPriceTTC: true,
    usePointDecimalSeparator: false,
    supportedPaymentFormatIds: ["ISO20022_PAIN001"],
};
